import { AudioFolderView } from "@/components/audioTracks/AudioFolderView";
import { GenerateLessonsModal } from "@/components/lessonGenerator/GenerateLessonsModal";
import { AddLessonModal } from "@/components/lessons/AddLessonModal";
import { LessonCard } from "@/components/lessons/LessonCard";
import { DuplicateItemModal } from "@/components/modal/DuplicateItemModal";
import { LinkedParentWarningModal } from "@/components/modal/LinkedParentWarningModal";
import { MoveModal } from "@/components/modal/MoveModal";
import { SelectionActionModal } from "@/components/modal/SelectionActionModal";
import { TrashModal } from "@/components/modal/TrashModal";
import { UnlinkModal } from "@/components/modal/UnlinkModal";
import { WarningModal } from "@/components/modal/WarningModal";
import { ActionButtons } from "@/components/projects/ActionButtons";
import { AddProjectModal } from "@/components/projects/AddProjectModal";
import { ProjectCard } from "@/components/projects/ProjectCard";
import { ProjectGroupsSection } from "@/components/projects/ProjectGroupsSection";
import { NoItemFound } from "@/components/shared/NoItemFound";
import { NotFound } from "@/components/shared/NotFound";
import { Skeleton } from "@/components/shared/Skeleton";
import { StickyAddButton } from "@/components/shared/StickyAddButton";
import {
  BreadcrumbSkeleton,
  LessonCardSkeleton,
  ProjectCardSkeleton,
} from "@/components/skeletons";
import { SelectionProvider, useSelection } from "@/contexts/SelectionContext";
import {
  GetProjectBreadcrumbsDocument,
  type LessonFieldsFragment,
  type ProjectFieldsFragment,
  ProjectKind,
  SortOrder,
} from "@/generated/graphql";
import { ITEM_LIMIT } from "@/lib/constants/shared";
import type { ReorderInfo } from "@/lib/dnd/components/Sortable";
import { Sortable } from "@/lib/dnd/components/Sortable";
import { useLessonsByParent } from "@/lib/hooks/lessons/useLessonsByParent";
import { useReorderLesson } from "@/lib/hooks/lessons/useReorderLesson";
import { useProjectsByParent } from "@/lib/hooks/projects/useProjectsByParent";
import { useReorderProject } from "@/lib/hooks/projects/useReorderProject";
import { useResponsiveSkeletonCount } from "@/lib/hooks/useResponsiveSkeletonCount";
import { useScrollRestoration } from "@/lib/hooks/useScrollRestoration";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { useDebouncedSearch } from "@/lib/utils/useDebouncedSearch";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { closeEditLessonModal, closeEditProjectModal } from "@/store/uiSlice";
import type { LessonFields } from "@/types/Lesson";
import type { ProjectFields } from "@/types/Project";
import type { ViewMode } from "@/types/viewMode";
import { ErrorCodes } from "@ai-teach/types";
import { Breadcrumb, Button, LoadMoreButton, SearchBar } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { MainSidebar } from "../../components/sidebars/MainSidebar";

function ProjectDetailPageContent() {
  const dispatch = useAppDispatch();
  const params = useParams({ from: "/admin/project/$projectId/" });
  const projectId = params.projectId;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [searchText, setsearchText] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);

  const navigate = useNavigate();

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === "grid" ? "list" : "grid"));
  };

  const debouncedSearch = useDebouncedSearch(searchText);

  const skeletonCount = useResponsiveSkeletonCount(2);

  const {
    data: projectBreadcrumbsData,
    loading: breadcrumbsLoading,
    error: breadcrumbsError,
  } = useQuery(GetProjectBreadcrumbsDocument, {
    variables: {
      id: projectId,
    },
    fetchPolicy: "cache-first",
  });

  const isNotFound = hasGraphQLErrorCode(breadcrumbsError, ErrorCodes.NOT_FOUND);

  if (isNotFound) {
    return (
      <NotFound
        title="Project not found"
        description="This project doesn't exist or the ID is invalid."
      />
    );
  }

  const projectBreadcrumbs = projectBreadcrumbsData?.project?.breadcrumbs;
  // An audio folder is a project, so it shares this route, breadcrumbs and trash — only
  // the body differs.
  const isAudioFolder = projectBreadcrumbsData?.project?.kind === ProjectKind.Audio;
  // Lets the header's "+" open AudioFolderView's file picker, so an audio folder keeps the
  // same add affordance as every other page instead of a menu the server would reject.
  const openAudioUpload = useRef<(() => void) | null>(null);

  const {
    projects: childProjects,
    hasMore: projectsHasMore,
    loading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects,
    loadMore: loadMoreProjects,
  } = useProjectsByParent({
    parentId: projectId,
    searchText: debouncedSearch,
    sortBy: SortOrder.Order,
  });

  const {
    lessons: childLessons,
    hasMore: lessonsHasMore,
    loading: lessonsLoading,
    error: lessonsError,
    refetch: refecthLessons,
    loadMore: loadMoreLessons,
  } = useLessonsByParent({
    parentId: projectId,
    searchText: debouncedSearch,
    sortBy: SortOrder.Order,
  });

  const { isEditProjectModalOpen, editingProject, isEditLessonModalOpen, editingLesson } =
    useAppSelector((state) => state.ui);

  const {
    isSelectionMode,
    isMoveModalOpen,
    isTrashModalOpen,
    isDuplicateLessonModalOpen,
    isDuplicateProjectModalOpen,
    isUnlinkModalOpen,
    isWarningModalOpen,
    isLinkedParentWarningModalOpen,
  } = useSelection();

  // Restore scroll to last visited item
  useScrollRestoration(projectId, projectsLoading || lessonsLoading);

  const { reorderLesson } = useReorderLesson();
  const { reorderProject } = useReorderProject();

  const onProjectReorder = (newItems: ProjectFieldsFragment[], { newIndex }: ReorderInfo) => {
    const movedProject = newItems[newIndex];
    const prev = newItems[newIndex - 1];
    const next = newItems[newIndex + 1];
    const prevSortOrder = prev?.sortOrder ?? null;
    const newSortOrder = next != null ? ((prevSortOrder ?? 0) + (next.sortOrder ?? 0)) / 2 : null;
    if (!movedProject) return;
    return reorderProject(movedProject as ProjectFields, projectId, prevSortOrder, newSortOrder);
  };

  const onLessonReorder = (newItems: LessonFieldsFragment[], { newIndex }: ReorderInfo) => {
    const movedLesson = newItems[newIndex];
    const prev = newItems[newIndex - 1];
    const next = newItems[newIndex + 1];
    const prevSortOrder = prev?.sortOrder ?? null;
    const newSortOrder = next != null ? ((prevSortOrder ?? 0) + (next.sortOrder ?? 0)) / 2 : null;
    if (!movedLesson) return;
    return reorderLesson(movedLesson as LessonFields, projectId, prevSortOrder, newSortOrder);
  };

  return (
    <div className="flex h-screen">
      <MainSidebar />

      {/* Main Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          {/* Breadcrumb Navigation */}
          {breadcrumbsLoading ? (
            <BreadcrumbSkeleton className="mb-4" />
          ) : (
            <Breadcrumb
              items={[
                {
                  label: "Home",
                  to: "/admin",
                },
                ...(projectBreadcrumbs?.map((breadcrumb) => ({
                  label: breadcrumb.name,
                  to: `/admin/project/${breadcrumb.id}`,
                })) ?? []),
              ]}
              className="mb-4"
            />
          )}

          {/* Project Title */}
          <h1 className="text-2xl font-bold w-[55%] truncate my-5">
            {breadcrumbsLoading ? (
              <span className="inline-block h-9 w-64 bg-slate-200 animate-pulse rounded" />
            ) : (
              (projectBreadcrumbs?.at(-1)?.name ?? "")
            )}
          </h1>

          {/* Page Header */}
          <div className="flex justify-between items-center mb-6">
            {/* Search Bar */}
            <SearchBar
              value={searchText}
              onChange={setsearchText}
              placeholder="Search..."
              className=""
            />
            {/* Header Actions. In an audio folder there is nothing to add through this menu
                (projects and lessons are rejected there) and nothing to switch views between —
                uploading and the track list both live in AudioFolderView. */}
            {isAudioFolder ? (
              <ActionButtons
                trashTo="/admin/project/$projectId/trash"
                trashParams={{ projectId }}
                trashCount={projectBreadcrumbsData?.project?.trashCount ?? 0}
                onAdd={() => openAudioUpload.current?.()}
              />
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/admin/project/$projectId/drafts" params={{ projectId }}>
                  <Button variant="ghost">Drafts</Button>
                </Link>
                <Button variant="secondary" onClick={() => setIsGenerateOpen(true)}>
                  Generate Lessons
                </Button>
                <ActionButtons
                  trashTo="/admin/project/$projectId/trash"
                  trashParams={{ projectId }}
                  trashCount={projectBreadcrumbsData?.project?.trashCount ?? 0}
                  viewMode={viewMode}
                  onToggleView={toggleViewMode}
                  parentId={projectId}
                />
              </div>
            )}
          </div>

          {/* Content Area */}
          {isAudioFolder ? (
            <AudioFolderView folderId={projectId} openUploadRef={openAudioUpload} />
          ) : (
            <>
              {/* Groups Section */}
              <ProjectGroupsSection projectId={projectId} />

              {/* Projects Section */}
              <>
                <h2 className="h4 mb-6">Projects</h2>
                <div className="mb-12">
                  <div
                    className={
                      viewMode === "grid"
                        ? ITEM_LIMIT.gridCardItemLimitClass
                        : ITEM_LIMIT.listCardItemLimitClass
                    }
                  >
                    {projectsLoading ? (
                      Array.from({ length: skeletonCount }).map((_, i) => (
                        <ProjectCardSkeleton
                          key={`project-skeleton-${i + 1}`}
                          viewMode={viewMode}
                        />
                      ))
                    ) : childProjects.length === 0 ? (
                      <NoItemFound
                        itemType="project"
                        error={projectsError}
                        refetch={refetchProjects}
                      />
                    ) : (
                      <Sortable
                        items={childProjects}
                        onReorder={(newOrders, info) => onProjectReorder(newOrders, info)}
                        disabled={isSelectionMode || debouncedSearch.trim() !== ""}
                      >
                        {(project, index) => (
                          <ProjectCard
                            key={project.id}
                            project={project as ProjectFields}
                            viewMode={viewMode}
                            url="projectDetail"
                            isSelectionMode={isSelectionMode}
                            index={index}
                          />
                        )}
                      </Sortable>
                    )}
                  </div>

                  {(projectsLoading || childProjects.length > 0) && (
                    <LoadMoreButton
                      onClick={loadMoreProjects}
                      hasMore={projectsHasMore}
                      loading={projectsLoading}
                    />
                  )}
                </div>
              </>

              {/* Lessons Section */}
              <>
                <h2 className="h4 mb-6">Lessons</h2>
                <div className="mb-12">
                  <div
                    className={
                      viewMode === "grid"
                        ? ITEM_LIMIT.gridCardItemLimitClass
                        : ITEM_LIMIT.listCardItemLimitClass
                    }
                  >
                    {lessonsLoading ? (
                      Array.from({ length: skeletonCount }).map((_, i) => (
                        <LessonCardSkeleton key={`lesson-skeleton-${i + 1}`} viewMode={viewMode} />
                      ))
                    ) : childLessons.length === 0 ? (
                      <NoItemFound
                        itemType="lesson"
                        error={lessonsError}
                        refetch={refecthLessons}
                      />
                    ) : (
                      <Sortable
                        items={childLessons}
                        onReorder={(newOrders, info) => onLessonReorder(newOrders, info)}
                        disabled={isSelectionMode || debouncedSearch.trim() !== ""}
                      >
                        {(lesson, index) => (
                          <LessonCard
                            lesson={lesson as LessonFields}
                            viewMode={viewMode}
                            url="lessonDetail"
                            isSelectionMode={isSelectionMode}
                            index={index}
                          />
                        )}
                      </Sortable>
                    )}
                  </div>

                  {(lessonsLoading || childLessons.length > 0) && (
                    <LoadMoreButton
                      onClick={loadMoreLessons}
                      hasMore={lessonsHasMore}
                      loading={lessonsLoading}
                    />
                  )}
                </div>
              </>
            </>
          )}
        </div>
      </div>

      {/* An audio folder cannot hold projects or lessons — the server rejects both — so the
          add menu would only ever offer choices that fail. Uploading happens in AudioFolderView. */}
      {!isAudioFolder && (
        <StickyAddButton parentId={projectId} scrollContainerRef={scrollContainerRef} />
      )}

      {/* Modals */}
      {isMoveModalOpen && (
        <MoveModal
          isOpen={isMoveModalOpen}
          defaultNavigationStack={projectBreadcrumbs?.map((b) => ({ id: b.id, name: b.name }))}
        />
      )}
      {editingProject && (
        <AddProjectModal
          isOpen={isEditProjectModalOpen}
          onClose={() => dispatch(closeEditProjectModal())}
          parentId={null}
          project={editingProject}
        />
      )}
      {editingLesson && (
        <AddLessonModal
          isOpen={isEditLessonModalOpen}
          onClose={() => dispatch(closeEditLessonModal())}
          parentId={null}
          lesson={editingLesson}
        />
      )}
      {isSelectionMode && <SelectionActionModal page="projectPage" isOpen={isSelectionMode} />}
      {isTrashModalOpen && <TrashModal isOpen={isTrashModalOpen} sourceId={projectId} />}
      {isDuplicateLessonModalOpen && (
        <DuplicateItemModal
          isOpen={true}
          itemType="lesson"
          defaultNavigationStack={projectBreadcrumbs?.map((b) => ({ id: b.id, name: b.name }))}
        />
      )}
      {isDuplicateProjectModalOpen && (
        <DuplicateItemModal
          isOpen={true}
          itemType="project"
          defaultNavigationStack={projectBreadcrumbs?.map((b) => ({ id: b.id, name: b.name }))}
        />
      )}
      {/* Mounted only once the breadcrumbs are known: the reference picker reads the project stack
          in a state initializer, so one arriving later would be ignored. */}
      {isGenerateOpen && !breadcrumbsLoading && (
        <GenerateLessonsModal
          isOpen={isGenerateOpen}
          onClose={() => setIsGenerateOpen(false)}
          projectId={projectId}
          onStarted={() => {
            void refecthLessons();
            void navigate({ to: "/admin/project/$projectId/drafts", params: { projectId } });
          }}
        />
      )}
      {isUnlinkModalOpen && <UnlinkModal isOpen={true} />}
      {isWarningModalOpen && <WarningModal isOpen={true} />}
      {isLinkedParentWarningModalOpen && (
        <LinkedParentWarningModal isOpen={isLinkedParentWarningModalOpen} />
      )}
    </div>
  );
}

export function ProjectDetailPage() {
  return (
    <SelectionProvider>
      <ProjectDetailPageContent />
    </SelectionProvider>
  );
}
